/**
 * CORE-1298 (возврат 01.10.2026) — как включать и выключать микрофон в комнате.
 *
 * Agora даёт два способа: `setEnabled` и `setMuted`. `setEnabled(false)`
 * отпускает микрофон, `setEnabled(true)` открывает его заново — и Safari на
 * Mac делает это заметно дольше остальных. Прод 01–02.10: первый слышимый кадр
 * у слушателей после включения микрофона говорящим из Safari/macOS — медиана
 * 1,2 с, у любого другого браузера (включая Safari на iPhone) — 0,2 с. В
 * комнате 383 (30.09) участница из Safari/macOS была слышна через 2,0–2,8 с
 * после каждого включения, а та же участница из Chrome — через 0,2 с.
 *
 * `setMuted` микрофон не отпускает — только перестаёт отправлять звук, — и
 * включение срабатывает сразу. Цена: пока человек «выключен», микрофон открыт и
 * индикатор записи в браузере горит. Владелец выбрал этот обмен только для
 * Safari на Mac (02.10.2026); остальные браузеры остаются на `setEnabled`.
 *
 * CORE-1601 (05.10.2026) — остальные браузеры: микрофон держится открытым
 * ПОСЛЕ ПЕРВОГО ВКЛЮЧЕНИЯ (`hold`). Прод 25.09–05.10: каждое включение через
 * `setEnabled` заново открывает устройство и заново публикует дорожку, а
 * слушатель заново подписывается; повторные нажатия заставали переключение
 * незавершённым через 2–11 с. Держать микрофон с самого входа владелец не
 * стал: открытый микрофон переводит Bluetooth-гарнитуру и iPhone в режим
 * разговора, и звук практики хуже у всех, включая тех, кто не скажет ни слова.
 * Поэтому: вход — устройство отпущено, первое включение — обычное, дальше —
 * `setMuted`; пропало право говорить — устройство снова отпускается.
 *
 * Agora запрещает два перехода: `setEnabled(false)` у заглушённой дорожки и
 * `setMuted(true)` у выключенной. Всё остальное можно — на этом и стоит `hold`.
 */

export type MicSwitchMode = 'enable' | 'mute' | 'hold';

export type MicSwitchStep = 'set_enabled' | 'set_muted';

type MicTrack = {
    enabled?: boolean;
    muted?: boolean;
    setEnabled: (on: boolean) => Promise<void> | void;
    setMuted?: (muted: boolean) => Promise<void> | void;
    /** Agora's device label — read to spot a Bluetooth headset (CORE-2042). */
    getTrackLabel?: () => string;
};

/**
 * CORE-2042 (02.11.2026) — a Bluetooth headset mic is let go on EVERY switch-off.
 *
 * While its mic is captured, macOS keeps the headset in the
 * hands-free call profile (HFP): playback drops to narrow-band, and the other
 * speakers sound far away and muffled. Prod, room 1292, 02.11: Chrome/macOS with
 * `bluetoothMic: true` ran its output at `contextSampleRate` 24000, while every
 * other Mac in the room ran 48000/44100. Holding such a mic open after the first
 * unmute (CORE-1601) or from the join (Safari/macOS, CORE-1298) kept the headset
 * in call mode between turns. Owner's decision: for a Bluetooth mic, release the
 * device on every switch-off, so the headset goes back to normal sound; the price
 * is 1–3 s on each switch-on while the headset changes profile. Every other mic
 * keeps its mode.
 */
export function micSwitchModeFor(track: Pick<MicTrack, 'getTrackLabel'>, mode: MicSwitchMode): MicSwitchMode {
    let label: string | undefined;
    try { label = track.getTrackLabel?.(); } catch { label = undefined; }
    return looksLikeBluetoothMic(label) ? 'enable' : mode;
}

/**
 * Safari на Mac — `mute` (держит микрофон с входа), всё остальное — `hold`.
 *
 * iPad в режиме «как компьютер» представляется Mac-ом; отличаем его по
 * сенсорному экрану (`maxTouchPoints > 1`) — iOS в замере быстрый, и держать
 * там микрофон открытым незачем. Chrome, Edge, Opera, Firefox и Яндекс на Mac
 * тоже пишут «Safari» в UA, поэтому их маркеры исключаются явно.
 */
export function resolveMicSwitchMode(userAgent: string | null | undefined, maxTouchPoints?: number | null): MicSwitchMode {
    const ua = userAgent || '';
    if (!/Macintosh/.test(ua)) return 'hold';
    if ((maxTouchPoints ?? 0) > 1) return 'hold';
    if (!/Safari\//.test(ua)) return 'hold';
    if (/Chrome|Chromium|CriOS|FxiOS|Firefox|Edg|OPR|YaBrowser/.test(ua)) return 'hold';
    return 'mute';
}

/**
 * Каким методом пойдёт переключение — по режиму и по состоянию дорожки.
 *
 * `hold`: отпущенная дорожка (`enabled === false`) включается через
 * `setEnabled` — это первое включение или включение после возврата права;
 * открытая дорожка дальше только заглушается и возвращается через `setMuted`.
 */
export function micSwitchStep(track: MicTrack, on: boolean, mode: MicSwitchMode): MicSwitchStep {
    if (typeof track.setMuted !== 'function') return 'set_enabled';
    mode = micSwitchModeFor(track, mode);
    // A track held by `setMuted` (prewarm, Safari join) is brought back by
    // `setMuted(false)` — `setEnabled(true)` on it would leave it silent.
    if (mode === 'enable') return track.muted === true ? 'set_muted' : 'set_enabled';
    if (mode === 'mute') return 'set_muted';
    if (mode === 'hold') return track.enabled === false ? 'set_enabled' : 'set_muted';
    return 'set_enabled';
}

/** Переключить дорожку микрофона выбранным для неё способом. */
export function switchMicTrack(track: MicTrack, on: boolean, mode: MicSwitchMode): Promise<void> | void {
    if (micSwitchStep(track, on, mode) === 'set_muted') return track.setMuted!(!on);
    return track.setEnabled(on);
}

/**
 * Выключить микрофон ТАК, ЧТОБЫ УСТРОЙСТВО ОСВОБОДИЛОСЬ: вход в комнату и
 * потеря права говорить. В режиме `mute` (Safari/macOS) устройство не
 * отпускается намеренно — там это просто заглушение.
 *
 * @returns `false` — дорожка заглушена через `setMuted`, выключить её Agora не
 * даст; вызывающий обязан закрыть её целиком, следующее включение создаст новую.
 */
export async function releaseMicTrack(track: MicTrack, mode: MicSwitchMode): Promise<boolean> {
    mode = micSwitchModeFor(track, mode);
    if (mode === 'mute' && typeof track.setMuted === 'function') {
        await track.setMuted(true);
        return true;
    }
    if (track.muted === true) return false;
    await track.setEnabled(false);
    return true;
}

/** Имя пути для телеметрии — чтобы прод-замер показывал, каким способом шло включение. */
export const micSwitchPath = (mode: MicSwitchMode): 'set_enabled' | 'set_muted' =>
    mode === 'mute' ? 'set_muted' : 'set_enabled';

/**
 * CORE-1601 — похоже ли устройство на беспроводную гарнитуру, по его названию.
 *
 * Открытие микрофона переводит Bluetooth-гарнитуру в режим разговора, и это
 * занимает секунды — главный подозреваемый в жалобах «нажал, а слышно не
 * сразу». Само название в телеметрию не уходит: только признак.
 */
export function looksLikeBluetoothMic(label: string | null | undefined): boolean {
    return /bluetooth|airpods|hands-?free|buds|беспровод/i.test(label || '');
}

/**
 * CORE-1298 — сколько живёт просьба открыть микрофон заранее. Слово выдали,
 * а право публиковать доезжает до клиента чуть позже (токен, роль) — просьба
 * ждёт этого. Но не дольше: просьба пятиминутной давности не должна открыть
 * микрофон, когда голос возвращается всей комнате после практики.
 */
export const MIC_PREWARM_WINDOW_MS = 30_000;

/**
 * Открывать ли микрофон заранее (заглушённым) по свежей выдаче слова.
 *
 * Только в режиме `hold`: Safari на Mac держит микрофон с входа. И только если
 * он сейчас не открыт — ни включённым, ни уже удерживаемым.
 */
export function shouldPrewarmMic(o: {
    requestedAt: number | null;
    now: number;
    mode: MicSwitchMode;
    /** `null` — дорожки нет вовсе. */
    track: { enabled?: boolean } | null;
}): boolean {
    if (o.requestedAt == null || o.now - o.requestedAt > MIC_PREWARM_WINDOW_MS) return false;
    if (o.mode !== 'hold') return false;
    return o.track == null || o.track.enabled === false;
}

/**
 * CORE-1615 — нажатие на микрофон, пока вход в эфир ещё идёт.
 *
 * Между publisher-токеном и открытием микрофона на входе проходит секунда-две.
 * Публикация в этом окне бросает «haven't joined yet», а вход затем открывает
 * свою дорожку и отпускает её — нажатие пропадало без следа. Такое нажатие не
 * исполняется сразу, а запоминается: вход сам выведет микрофон в эфир.
 *
 * Когда дорожка уже есть, откладывать нечего — работает обычное переключение.
 *
 * Возврат QA 06.10.2026 — окно начинается раньше токена: пока вход не узнал
 * свою роль (`joinTokenPending`), нажатие отбрасывалось как «нет права», хотя
 * экран говорить уже разрешал. После перезагрузки страницы панель кнопок
 * появляется именно в этот момент.
 */
export function shouldDeferMicPress(input: { joinMicPending: boolean; joinTokenPending?: boolean; hasTrack: boolean }): boolean {
    return (input.joinMicPending || !!input.joinTokenPending) && !input.hasTrack;
}

/**
 * CORE-1645 — the window in which a `setMuted` switch may have gone into a dead
 * signalling socket.
 *
 * `setMuted` is one message on Agora's signalling socket; the room does not
 * acknowledge it and the SDK does not repeat it after a reconnect. A socket
 * that the network has frozen stays «connected» until the SDK's own timeout,
 * and a switch made in that gap is lost while media keeps flowing: stand ag1,
 * 08.10.2026, rooms 1003121 / 1003123 / 1003124 — mic switched on, bytes
 * leaving, the listener receiving none; the socket reconnected 4–8 s after the
 * press in every one of them.
 *
 * 30 s covers the slowest gap seen (8 s) several times over and still leaves a
 * person who switched the mic a minute ago alone: re-announcing costs a
 * speaker a fraction of a second of sound.
 */
export const MIC_REANNOUNCE_WINDOW_MS = 30_000;

/**
 * After the signalling socket came back: does the room have to be told the
 * state of the mic again? Only a `setMuted` switch is in question — `setEnabled`
 * publishes and unpublishes, and those the SDK waits an answer for.
 */
export function shouldReannounceMicState(input: {
    /** When the mic was last switched by `setMuted`; `null` — never. */
    lastMutedSwitchAt: number | null;
    /** When the socket was seen dropping (`RECONNECTING`). */
    reconnectingAt: number;
    /** The track can be published at all (enabled) and is on the wire. */
    publishable: boolean;
    onWire: boolean;
}): boolean {
    if (input.lastMutedSwitchAt === null) return false;
    if (!input.publishable || !input.onWire) return false;
    const gap = input.reconnectingAt - input.lastMutedSwitchAt;
    return gap >= 0 && gap <= MIC_REANNOUNCE_WINDOW_MS;
}
